/**
 * ChatInterfaceV2 - Modern Chat Interface with Event-Sourced Backend
 * 
 * Features:
 * - Event-sourced persistence via /chat/v2 API
 * - Client-side transcription with Web Speech API
 * - Idempotency for reliable message delivery
 * - Optimistic UI updates
 * - Error handling and retry
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Send, Bot, User, Mic, MicOff, AlertCircle } from 'lucide-react';
import { ChatV2Service } from '../services/chatV2Service';
import './ChatInterface.css';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  turn_id?: string;
  status?: 'sending' | 'sent' | 'error';
  error?: string;
  latency_ms?: number;
}

interface ChatInterfaceV2Props {
  familyId: string | null;
  apiBase: string;
  onMessageSent?: (message: string) => void;
}

export const ChatInterfaceV2: React.FC<ChatInterfaceV2Props> = ({
  familyId,
  apiBase,
  onMessageSent
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Transcription state
  const [isRecording, setIsRecording] = useState(false);
  const [transcriptionSupported, setTranscriptionSupported] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatServiceRef = useRef<ChatV2Service | null>(null);
  const recognitionRef = useRef<any>(null);
  const currentIdempotencyKeyRef = useRef<string | null>(null);
  const micPressTimerRef = useRef<number | null>(null);
  const isHoldModeRef = useRef<boolean>(false);
  const typingStartTimeRef = useRef<number | null>(null);
  const ignoreRecognitionResultsRef = useRef<boolean>(false);

  // Initialize chat service
  useEffect(() => {
    chatServiceRef.current = new ChatV2Service(apiBase);
  }, [apiBase]);

  // Check for Web Speech API support
  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || 
                               (window as any).webkitSpeechRecognition;
    
    if (SpeechRecognition) {
      setTranscriptionSupported(true);
      
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      
      recognition.onresult = (event: any) => {
        // Ignore results if we're in the process of sending
        if (ignoreRecognitionResultsRef.current) {
          return;
        }
        
        let interim = '';
        let final = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            final += transcript + ' ';
          } else {
            interim += transcript;
          }
        }
        
        if (final) {
          setInputValue(prev => prev + final);
          setInterimTranscript('');
        } else {
          setInterimTranscript(interim);
        }
      };
      
      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
        setInterimTranscript('');
        
        if (event.error === 'not-allowed') {
          setError('Microphone access denied. Please enable microphone permissions.');
        }
      };
      
      recognition.onend = () => {
        // Check current recording state and restart if still recording
        setIsRecording(prev => {
          if (prev && recognitionRef.current) {
            try {
              recognitionRef.current.start();
            } catch (err) {
              console.error('Failed to restart recognition:', err);
              return false;
            }
          }
          return prev;
        });
      };
      
      recognitionRef.current = recognition;
    }
    
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (err) {
          // Ignore errors on cleanup
        }
      }
    };
  }, []); // Run only once on mount

  const scrollToBottom = () => {
    if (messagesEndRef.current && typeof messagesEndRef.current.scrollIntoView === 'function') {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadChatHistory = useCallback(async () => {
    if (!familyId || !chatServiceRef.current) return;

    try {
      const history = await chatServiceRef.current.getHistory(familyId, familyId);
      
      const formattedMessages: Message[] = history.map(msg => ({
        id: msg.id,
        role: msg.role,
        text: msg.text,
        timestamp: msg.timestamp,
        turn_id: msg.turn_id,
        status: 'sent'
      }));

      setMessages(formattedMessages);
      setError(null);
    } catch (err) {
      console.error('Failed to load chat history:', err);
      setError('Failed to load chat history');
    }
  }, [familyId]);

  useEffect(() => {
    if (familyId) {
      loadChatHistory();
    } else {
      setMessages([]);
    }
  }, [familyId, loadChatHistory]);

  const handleSendMessage = async (retryIdempotencyKey?: string) => {
    // Set flag to ignore any pending recognition results
    ignoreRecognitionResultsRef.current = true;
    
    // Finalize any interim transcript before stopping
    let finalText = inputValue;
    if (interimTranscript.trim()) {
      finalText = inputValue + (inputValue ? ' ' : '') + interimTranscript;
    }
    
    // Stop recording if active
    if (isRecording && recognitionRef.current) {
      try {
        recognitionRef.current.stop();
        setIsRecording(false);
      } catch (err) {
        console.error('Failed to stop recording:', err);
      }
    }
    
    if (!finalText.trim() || !familyId || !chatServiceRef.current) return;

    const messageText = finalText.trim();
    const conversationId = familyId;
    const idempotencyKey = retryIdempotencyKey || chatServiceRef.current['generateIdempotencyKey']();
    
    currentIdempotencyKeyRef.current = idempotencyKey;
    
    // Clear input and interim transcript
    setInputValue('');
    setInterimTranscript('');
    typingStartTimeRef.current = Date.now();
    setIsTyping(true);
    setError(null);

    // Optimistic user message - capture actual send time
    const optimisticId = `temp_${Date.now()}`;
    const userSendTime = new Date(); // Capture when user actually sent the message
    const userMessage: Message = {
      id: optimisticId,
      text: messageText,
      timestamp: userSendTime,
      role: 'user',
      status: 'sending'
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      // Mark user message as sent
      setMessages(prev => prev.map(msg => 
        msg.id === optimisticId 
          ? { ...msg, status: 'sent' as const }
          : msg
      ));

      // Create streaming assistant message placeholder
      const assistantId = `assistant_${Date.now()}`;
      const assistantMessage: Message = {
        id: assistantId,
        text: '',
        timestamp: new Date(),
        role: 'assistant',
        status: 'sending'
      };
      setMessages(prev => [...prev, assistantMessage]);
      setStreamingMessageId(assistantId);
      setIsTyping(false); // Hide typing indicator, show streaming text instead

      // Stream the response
      await chatServiceRef.current.sendMessageStream(
        familyId,
        conversationId,
        messageText,
        (chunk: string) => {
          // Update assistant message with each chunk
          setMessages(prev => prev.map(msg => 
            msg.id === assistantId
              ? { ...msg, text: msg.text + chunk }
              : msg
          ));
        },
        idempotencyKey
      );

      // Mark as complete
      const latencyMs = Date.now() - (typingStartTimeRef.current || Date.now());
      setMessages(prev => prev.map(msg => 
        msg.id === assistantId
          ? { ...msg, status: 'sent' as const, latency_ms: latencyMs }
          : msg
      ));
      
      setStreamingMessageId(null);
      ignoreRecognitionResultsRef.current = false;

      if (onMessageSent) {
        onMessageSent(messageText);
      }
      
      currentIdempotencyKeyRef.current = null;
    } catch (err: any) {
      setStreamingMessageId(null);
      console.error('Failed to send message:', err);
      
      // Mark message as error
      setMessages(prev => prev.map(msg => 
        msg.id === optimisticId 
          ? { ...msg, status: 'error' as const, error: err.message }
          : msg
      ));
      
      setIsTyping(false);
      setError(`Failed to send message: ${err.message}`);
    }
  };

  const handleRetry = (messageId: string) => {
    const message = messages.find(m => m.id === messageId);
    if (!message || !currentIdempotencyKeyRef.current) return;
    
    setInputValue(message.text);
    handleSendMessage(currentIdempotencyKeyRef.current);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const startRecording = () => {
    if (!recognitionRef.current || isRecording) return;
    
    try {
      recognitionRef.current.start();
      setIsRecording(true);
      setError(null);
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError('Failed to start microphone');
    }
  };

  const stopRecording = () => {
    if (!recognitionRef.current || !isRecording) return;
    
    try {
      recognitionRef.current.stop();
      setIsRecording(false);
      setInterimTranscript('');
    } catch (err) {
      console.error('Failed to stop recording:', err);
    }
  };

  const handleMicMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    
    // Clear any existing timer
    if (micPressTimerRef.current) {
      clearTimeout(micPressTimerRef.current);
    }
    
    // Start a timer to detect hold vs click
    micPressTimerRef.current = window.setTimeout(() => {
      // If we get here, user is holding the button
      isHoldModeRef.current = true;
      if (!isRecording) {
        startRecording();
      }
    }, 200); // 200ms threshold to distinguish hold from click
  };

  const handleMicMouseUp = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    
    // Clear the timer
    if (micPressTimerRef.current) {
      clearTimeout(micPressTimerRef.current);
      micPressTimerRef.current = null;
    }
    
    // If in hold mode, stop recording
    if (isHoldModeRef.current) {
      isHoldModeRef.current = false;
      stopRecording();
    }
    // Otherwise, this was a quick click - toggle handled by onClick
  };

  const handleMicClick = (e: React.MouseEvent) => {
    // Only toggle if this wasn't a hold operation
    if (!isHoldModeRef.current) {
      if (isRecording) {
        stopRecording();
      } else {
        startRecording();
      }
    }
  };

  const handleMicMouseLeave = () => {
    // Clear timer if mouse leaves during press
    if (micPressTimerRef.current) {
      clearTimeout(micPressTimerRef.current);
      micPressTimerRef.current = null;
    }
    
    // If in hold mode, stop recording when mouse leaves
    if (isHoldModeRef.current) {
      isHoldModeRef.current = false;
      stopRecording();
    }
  };

  if (!familyId) {
    return (
      <div className="chat-placeholder">
        <div className="chat-placeholder-content">
          <Bot className="chat-placeholder-icon" />
          <p className="chat-placeholder-text">Select a family to start chatting</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-interface">
      {/* Chat Header */}
      <div className="chat-header">
        <div className="chat-header-content">
          <div className="chat-avatar">
            <Bot size={20} />
          </div>
          <div className="chat-header-info">
            <h3>AI Assistant</h3>
            <p>Event-sourced chat (v2)</p>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="error-banner" style={{
          padding: '12px',
          backgroundColor: '#fee',
          color: '#c33',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '14px'
        }}>
          <AlertCircle size={16} />
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '18px'
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Messages Area */}
      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-empty">
            <Bot size={48} />
            <p>No messages yet. Start a conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`message-row ${message.role}`}
            >
              {message.role === 'assistant' && (
                <div className="message-avatar">
                  <Bot size={16} />
                </div>
              )}
              
              <div className={`message-bubble ${message.role} ${message.status === 'error' ? 'error' : ''}`}>
                <p className="message-text">{message.text}</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <p className="message-time">
                    {new Date(message.timestamp).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      second: '2-digit',
                    })}
                  </p>
                  {message.role === 'assistant' && message.latency_ms && (
                    <span style={{ fontSize: '11px', opacity: 0.6 }}>
                      ({(message.latency_ms / 1000).toFixed(1)}s)
                    </span>
                  )}
                  {message.status === 'sending' && (
                    <span style={{ fontSize: '11px', opacity: 0.6 }}>Sending...</span>
                  )}
                  {message.status === 'error' && (
                    <button
                      onClick={() => handleRetry(message.id)}
                      style={{
                        fontSize: '11px',
                        color: '#c33',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        textDecoration: 'underline'
                      }}
                    >
                      Retry
                    </button>
                  )}
                </div>
              </div>

              {message.role === 'user' && (
                <div className="message-avatar">
                  <User size={16} />
                </div>
              )}
            </div>
          ))
        )}
        
        {isTyping && (
          <div className="typing-indicator">
            <div className="message-avatar">
              <Bot size={16} />
            </div>
            <div className="typing-dots">
              <div className="typing-dot" />
              <div className="typing-dot" />
              <div className="typing-dot" />
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="chat-input-area">
        <div className="chat-input-container">
          <textarea
            value={inputValue + (interimTranscript ? ` ${interimTranscript}` : '')}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isRecording ? "Listening..." : "Type your message..."}
            className="chat-textarea"
            rows={1}
            disabled={isRecording}
          />
          
          {/* Microphone Button */}
          {transcriptionSupported && (
            <button
              onClick={handleMicClick}
              onMouseDown={handleMicMouseDown}
              onMouseUp={handleMicMouseUp}
              onMouseLeave={handleMicMouseLeave}
              onTouchStart={handleMicMouseDown}
              onTouchEnd={handleMicMouseUp}
              className={`chat-mic-button ${isRecording ? 'recording' : ''}`}
              aria-label={isRecording ? "Recording... (Click to stop or press Send)" : "Click to start/stop or hold to record"}
              title={isRecording ? "Recording... (Click to stop or press Send)" : "Click to start/stop or hold to record"}
              style={{
                background: isRecording ? '#ef4444' : '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                userSelect: 'none',
                WebkitUserSelect: 'none'
              }}
            >
              {isRecording ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
          )}
          
          {!transcriptionSupported && (
            <div
              title="Voice input not supported in this browser"
              style={{
                padding: '10px',
                opacity: 0.3,
                cursor: 'not-allowed'
              }}
            >
              <MicOff size={16} />
            </div>
          )}
          
          <button
            onClick={() => handleSendMessage()}
            disabled={!inputValue.trim() && !interimTranscript.trim()}
            className="chat-send-button"
            aria-label="Send message"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};
